import {
  Alert,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { isNil } from "lodash";
import type React from "react";
import { useCallback, useRef, useState } from "react";
import {
  IngestionTestRunMode,
  useCreateIngestionTestUploadMutation,
  useTriggerAdhocPreviewMutation,
  useTriggerIngestionTestRunMutation,
} from "../../generated/graphql";
import CompanySelect from "../doc-scanning/company-select";
import { type SelectOption } from "../doc-scanning/schema-testing/types";
import IngestionTestRunDetail from "../ingestion-test-runs/ingestion-test-run-detail";
import AdhocPreviewResult from "./adhoc-preview-result";

/** S3 requires a concrete content type; browsers occasionally report none. */
const DEFAULT_MIME_TYPE = "application/octet-stream";

const styles = {
  paper: { p: 2 },
  gridSpacer: { mt: 0.5 },
  sectionDivider: { my: 2 },
  actions: { mt: 2 },
  errorAlert: { mt: 2 },
  warningAlert: { mt: 1 },
} satisfies Record<string, SxProps<Theme>>;

/**
 * One in-flight (or failed) trigger attempt: the uploaded object and the
 * idempotency key minted for it. Reused verbatim on retry so a double-submit of
 * the SAME attempt replays server-side rather than creating a duplicate run;
 * cleared on success or whenever an input changes (which starts a new attempt).
 */
type Attempt = {
  uploadId: string;
  idempotencyKey: string;
};

/**
 * The ad-hoc ingestion preview tool: pick a company and a local document,
 * optionally simulate the ingesting email, upload the file (presigned PUT), and
 * trigger a run. By default this is a side-effect-free preview whose run id
 * drives {@link AdhocPreviewResult}, which polls for the terminal state and
 * renders the panels. With the "Create real scanned order" toggle on, the same
 * upload is instead dispatched through the authoritative V3 apply path
 * (FULL_APPLY), which creates a real scanned order and order downstream —
 * visible in the TMS like any production intake and retained past the Track-2
 * cleanup window; the toggle defaults off. Its run id drives
 * {@link IngestionTestRunDetail}, which polls that run to its terminal state
 * the same way the preview path does.
 */
const AdhocPreviewTool = () => {
  const [companyUuid, setCompanyUuid] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [simulateEmail, setSimulateEmail] = useState(false);
  const [fromAddress, setFromAddress] = useState("");
  const [subject, setSubject] = useState("");
  const [receivedAt, setReceivedAt] = useState("");
  // Defaults OFF: the tool is a dry preview unless the operator explicitly opts
  // into the authoritative apply. Only when this is on does the client send
  // confirmFullApply — the server-side guard remains the real authority.
  const [createRealOrder, setCreateRealOrder] = useState(false);

  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  // The authoritative (FULL_APPLY) run's id, once dispatched. Drives
  // {@link IngestionTestRunDetail}, which polls the run to its terminal state
  // just like the preview path — the real scanned order it creates settles
  // asynchronously downstream and the detail's lifecycle timeline tracks it.
  const [authoritativeRunId, setAuthoritativeRunId] = useState<string | null>(
    null,
  );

  // The current attempt's upload + idempotency key. Held in a ref because it
  // drives imperative submission, not rendering: a failed trigger keeps it so
  // the next click replays; success or an input change clears it (below).
  const attemptRef = useRef<Attempt | null>(null);

  const [createUpload] = useCreateIngestionTestUploadMutation();
  const [triggerPreview] = useTriggerAdhocPreviewMutation();
  const [triggerTestRun] = useTriggerIngestionTestRunMutation();

  // Any input change abandons a pending (failed) attempt so the next submit is a
  // fresh upload + key rather than a replay of the previous inputs' attempt, AND
  // clears the previously-rendered run — an old company's completed decision must
  // never sit beside newly-changed inputs.
  const resetAttempt = useCallback(() => {
    attemptRef.current = null;
    setRunId(null);
    setAuthoritativeRunId(null);
  }, []);

  const handleCompanyChange = useCallback(
    (option: SelectOption | undefined | null) => {
      setCompanyUuid(option?.value ?? null);
      resetAttempt();
    },
    [resetAttempt],
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFile(event.target.files?.[0] ?? null);
      resetAttempt();
    },
    [resetAttempt],
  );

  const fromAddressTrimmed = fromAddress.trim();
  const emailSectionValid = !simulateEmail || fromAddressTrimmed.length > 0;
  const canSubmit =
    !isNil(companyUuid) && !isNil(file) && emailSectionValid && !busy;

  const handleRun = async () => {
    if (isNil(companyUuid) || isNil(file)) {
      return;
    }
    setSubmitError(null);
    setBusy(true);
    // Drop any prior run's result the moment a new attempt starts, so the panels
    // never show a stale run while this one uploads and triggers.
    setRunId(null);
    setAuthoritativeRunId(null);
    // Sniff once and reuse for both the presign request and the PUT: the URL is
    // signed for this exact content type, so the PUT header must match it.
    const mimeType = file.type === "" ? DEFAULT_MIME_TYPE : file.type;
    try {
      // Reuse the pending attempt's upload + key on retry; only upload afresh
      // when there is no attempt in flight yet.
      if (attemptRef.current === null) {
        const uploadRes = await createUpload({
          variables: {
            input: {
              companyUuid,
              fileName: file.name,
              mimeType,
              sizeBytes: file.size,
            },
          },
        });
        const upload = uploadRes.data?.createIngestionTestUpload;
        if (isNil(upload)) {
          throw new Error("Upload could not be created.");
        }
        // Raw-bytes PUT to the presigned URL. No credentials — the S3 URL is
        // pre-authorized and must not receive the internal session cookie.
        const putRes = await fetch(upload.uploadUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": mimeType },
          credentials: "omit",
        });
        if (!putRes.ok) {
          throw new Error(
            `Uploading the file failed (${putRes.status} ${putRes.statusText}).`,
          );
        }
        attemptRef.current = {
          uploadId: upload.uploadId,
          idempotencyKey: crypto.randomUUID(),
        };
      }

      const attempt = attemptRef.current;
      const emailSimulation = simulateEmail
        ? {
            fromAddress: fromAddressTrimmed,
            ...(subject.trim() === "" ? {} : { subject: subject.trim() }),
            ...(receivedAt === ""
              ? {}
              : { receivedAt: new Date(receivedAt).toISOString() }),
          }
        : undefined;

      // Authoritative path: route the same upload through the REAL V3 apply
      // spine (FULL_APPLY), which creates a real scanned order downstream.
      // `confirmFullApply` is sent ONLY here — reached only by the explicit
      // toggle opt-in — and the server re-checks it before applying, so the
      // client flag never bypasses the guard. Idempotent on the same key.
      if (createRealOrder) {
        const runRes = await triggerTestRun({
          variables: {
            input: {
              uploadId: attempt.uploadId,
              companyUuid,
              idempotencyKey: attempt.idempotencyKey,
              mode: IngestionTestRunMode.FullApply,
              confirmFullApply: true,
              ...(isNil(emailSimulation) ? {} : { emailSimulation }),
            },
          },
        });
        const run = runRes.data?.triggerIngestionTestRun;
        if (isNil(run)) {
          throw new Error("The authoritative run could not be triggered.");
        }
        // Success — the next run is a distinct attempt with its own key.
        attemptRef.current = null;
        setAuthoritativeRunId(run.id);
        return;
      }

      const triggerRes = await triggerPreview({
        variables: {
          input: {
            uploadId: attempt.uploadId,
            companyUuid,
            idempotencyKey: attempt.idempotencyKey,
            ...(isNil(emailSimulation) ? {} : { emailSimulation }),
          },
        },
      });
      const triggered = triggerRes.data?.triggerAdhocPreview;
      if (isNil(triggered)) {
        throw new Error("The preview run could not be triggered.");
      }
      // Success — the next run is a distinct attempt with its own key.
      attemptRef.current = null;
      setRunId(triggered.runId);
    } catch (error) {
      // Leave the attempt in place (if one was recorded) so clicking Run again
      // replays the same trigger rather than duplicating the run.
      setSubmitError(error instanceof Error ? error.message : "Unknown error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={styles.paper}>
        <Typography gutterBottom variant="h6">
          New preview
        </Typography>

        <Grid container spacing={2} sx={styles.gridSpacer}>
          <Grid item xs={12} sm={6}>
            <CompanySelect
              currentCompanyUuid={companyUuid}
              onChange={handleCompanyChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack spacing={1}>
              <Button variant="outlined" component="label">
                {isNil(file) ? "Choose file" : "Change file"}
                <input hidden type="file" onChange={handleFileChange} />
              </Button>
              <Typography variant="caption" color="text.secondary">
                {isNil(file)
                  ? "No file selected."
                  : `${file.name} (${file.type === "" ? DEFAULT_MIME_TYPE : file.type})`}
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={styles.sectionDivider} />

        <FormControlLabel
          control={
            <Switch
              checked={simulateEmail}
              onChange={(e) => {
                setSimulateEmail(e.target.checked);
                resetAttempt();
              }}
            />
          }
          label="Simulate ingesting email"
        />
        {simulateEmail && (
          <Grid container spacing={2} sx={styles.gridSpacer}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                size="small"
                label="From address"
                value={fromAddress}
                error={fromAddressTrimmed.length === 0}
                helperText={
                  fromAddressTrimmed.length === 0
                    ? "Required when simulating an email."
                    : " "
                }
                onChange={(e) => {
                  setFromAddress(e.target.value);
                  resetAttempt();
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Subject (optional)"
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  resetAttempt();
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                label="Received at (optional)"
                InputLabelProps={{ shrink: true }}
                value={receivedAt}
                onChange={(e) => {
                  setReceivedAt(e.target.value);
                  resetAttempt();
                }}
              />
            </Grid>
          </Grid>
        )}

        <Divider sx={styles.sectionDivider} />

        <FormControlLabel
          control={
            <Switch
              color="warning"
              checked={createRealOrder}
              onChange={(e) => {
                setCreateRealOrder(e.target.checked);
                resetAttempt();
              }}
            />
          }
          label="Create real scanned order (authoritative)"
        />
        {createRealOrder && (
          <Alert severity="warning" sx={styles.warningAlert}>
            This routes the upload through the authoritative V3 apply path and
            creates a REAL scanned order for the selected company. It appears in
            the TMS scanned-orders queue (and, once applied, as a real order)
            exactly like a production upload, and is never auto-purged. Leave
            this off for a side-effect-free preview.
          </Alert>
        )}

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={styles.actions}
        >
          <Button
            variant="contained"
            color={createRealOrder ? "warning" : "primary"}
            disabled={!canSubmit}
            onClick={() => {
              void handleRun();
            }}
          >
            {createRealOrder
              ? busy
                ? "Creating order…"
                : "Upload & create real order"
              : busy
                ? "Running…"
                : "Upload & run preview"}
          </Button>
        </Stack>

        {submitError !== null && (
          <Alert severity="error" sx={styles.errorAlert}>
            {submitError} Retrying replays the same attempt rather than
            duplicating the run.
          </Alert>
        )}
      </Paper>

      {runId !== null && <AdhocPreviewResult runId={runId} />}

      {authoritativeRunId !== null && (
        <Stack spacing={2}>
          <Alert severity="warning">
            A real scanned order is being created for this company through the
            authoritative V3 apply path. It settles asynchronously downstream;
            the run below is polled to its terminal state.
          </Alert>
          <IngestionTestRunDetail runId={authoritativeRunId} />
        </Stack>
      )}
    </Stack>
  );
};

export default AdhocPreviewTool;
